import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  Wrench,
  FileText,
  Bell,
  Plus,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';
import { StatCard } from '../components/StatCard';
import { QuickActionCard } from '../components/QuickActionCard';
import { NotificationItem } from '../components/NotificationItem';
import { RecentActivityItem } from '../components/RecentActivityItem';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    tenants: 0,
    serviceRequests: 0,
    unreadMessages: 0
  });

  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch properties count
        const propsRes = await authFetch(getApiUrl('/api/v1/property/list'));
        if (propsRes.ok) {
          const propsData = await propsRes.json();
          setStats(prev => ({ ...prev, properties: propsData.properties?.length || 0 }));
        }

        // Fetch unread messages count
        const unreadRes = await authFetch(getApiUrl('/api/v1/message/unread-count'));
        if (unreadRes.ok) {
          const unreadData = await unreadRes.json();
          setStats(prev => ({ ...prev, unreadMessages: unreadData.unread_count || 0 }));
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };

    fetchStats();
  }, []);

  // Stats cards data
  const statsData = [
    { label: 'Properties', value: String(stats.properties), icon: Home, color: 'text-emerald-600' },
    { label: 'Active Tenants', value: String(stats.tenants), icon: MessageSquare, color: 'text-blue-500' },
    { label: 'Open Service Requests', value: String(stats.serviceRequests), icon: Wrench, color: 'text-amber-500' },
    { label: 'Unread Messages', value: String(stats.unreadMessages), icon: Bell, color: 'text-red-500' },
  ];

  const notifications = [
    {
      id: 1,
      tenant: 'John Doe',
      property: '123 Main St, Apt 2B',
      message: 'Refrigerator making strange noise',
      time: '2 hours ago',
      type: 'service',
      unread: true
    },
    {
      id: 2,
      tenant: 'Sarah Smith',
      property: '456 Oak Ave',
      message: 'When will the HVAC be serviced?',
      time: '5 hours ago',
      type: 'message',
      unread: true
    },
  ];

  const recentActivity = [
    {
      id: 1,
      action: 'Service request completed',
      description: 'Plumbing repair at 789 Elm St',
      time: '1 day ago',
      status: 'completed'
    },
    {
      id: 2,
      action: 'New tenant message',
      description: 'Question about lease terms',
      time: '2 days ago',
      status: 'pending'
    },
    {
      id: 3,
      action: 'Property inspection scheduled',
      description: '123 Main St - Annual inspection',
      time: '3 days ago',
      status: 'scheduled'
    },
  ];

  return (
    <Layout
      pageTitle={`Welcome back, ${localStorage.getItem('first_name') || ''}!`}
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsData.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <QuickActionCard
                icon={Plus}
                title="Add Property"
                description="Register a new rental property"
                color="bg-[#16A34A]"
                onClick={() => navigate('/addproperty')}
              />
              <QuickActionCard
                icon={Wrench}
                title="Add Appliance"
                description="Track appliances at properties"
                color="bg-[#3498DB]"
                onClick={() => navigate('/maintenance')}
              />
              <QuickActionCard
                icon={FileText}
                title="Create Service Request"
                description="Log a new maintenance issue"
                color="bg-[#F59E0B]"
                onClick={() => navigate('/service')}
              />
              <QuickActionCard
                icon={Sparkles}
                title="AI Diagnostics"
                description="Get help diagnosing issues"
                color="bg-[#8B5CF6]"
                onClick={() => navigate('/maintenance')}
              />
              <QuickActionCard
                icon={MessageSquare}
                title="Message Tenant"
                description="Send SMS to your tenants"
                color="bg-[#06B6D4]"
                onClick={() => navigate('/messages')}
              />
              <QuickActionCard
                icon={Home}
                title="View All Properties"
                description="Manage your rental portfolio"
                color="bg-[#64748B]"
                onClick={() => navigate('/properties')}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <p className="text-[#7F8C8D] text-center py-8">No recent activity to display.</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <RecentActivityItem key={activity.id} {...activity} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notifications from Tenants */}
        <div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C3E50]">Tenant Notifications</h3>
              <span className="bg-[#E74C3C] text-white text-xs font-semibold px-2 py-1 rounded-full">
                {notifications.filter(n => n.unread).length}
              </span>
            </div>
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto text-[#16A34A] mb-2" size={48} />
                <p className="text-[#7F8C8D]">All caught up!</p>
                <p className="text-sm text-[#7F8C8D]">No new notifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} {...notification} />
                ))}
                <button className="w-full text-center text-[#16A34A] hover:text-[#27AE60] font-medium py-2 text-sm transition-colors">
                  View All Notifications
                </button>
              </div>
            )}
          </div>

          {/* Tips Card */}
          <div className="bg-gradient-to-br from-[#16A34A] to-[#27AE60] rounded-lg shadow-sm p-6 mt-6 text-white">
            <div className="flex items-start gap-3">
              <Sparkles size={24} className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">Pro Tip</h4>
                <p className="text-sm text-white/90">
                  Use AI Diagnostics to quickly identify appliance issues before scheduling a service visit.
                  Save time and money!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
